import React, { useEffect, useState } from 'react';

interface OpPageHeaderProps {
  label: string;
  line1: string;
  line2?: string;
  description?: string;
  children?: React.ReactNode;
}

/**
 * Shared Optimus-style page header for sub-pages: mono eyebrow, big serif
 * headline (second line muted), optional description, optional extra content.
 */
const OpPageHeader: React.FC<OpPageHeaderProps> = ({ label, line1, line2, description, children }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-36 lg:pt-44 pb-12 lg:pb-16">
      <span
        className={`inline-flex items-center gap-3 text-sm font-mono text-muted-foreground mb-6 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        <span className="w-8 h-px bg-foreground/30" />
        {label}
      </span>
      <h1
        className={`text-5xl md:text-6xl lg:text-7xl font-display tracking-tight leading-[0.95] transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {line1}
        {line2 && (
          <>
            <br />
            <span className="text-muted-foreground">{line2}</span>
          </>
        )}
      </h1>
      {description && (
        <p
          className={`mt-8 text-xl text-muted-foreground leading-relaxed max-w-2xl transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          {description}
        </p>
      )}
      {children}
    </div>
  );
};

export default OpPageHeader;
